import moment, { Moment } from 'moment';
import { toMoment } from '@shared/utils/date';
import PeriodScheduleItem from '@api/models/period-schedule-item';
import type { PeriodSchedule } from '../getFixtureDetails';

// Moment does not support natively adding / subtracting fractional days / months,
// so we need to do some converting first
const addDuration = (
	from: Moment | Date | string | number,
	value: number,
	unit: moment.unitOfTime.DurationConstructor,
) => {
	let v = value;
	let u = unit;

	if (moment.normalizeUnits(unit) === 'year') {
		v = value * 12;
		u = 'months';
	}

	const whole = Math.trunc(v);
	const result = toMoment(from).add(whole, u);
	const frac = v - whole;

	return frac ? 
		result.add(moment.duration(frac, u).asMilliseconds(), 'milliseconds') : 
		result;
};

const calculateSchedule = ({
	items,
	laycanTo,
	laycanFrom,
	deliveryDate,
	useVariance = true,
}: {
	items: PeriodScheduleItem[];
	deliveryDate?: Moment | null;
	laycanTo: Moment;
	laycanFrom?: Moment | null;
	useVariance?: boolean;
}) => {
	const sortedItems = items.sort((a, b) => a.order - b.order);
	const schedule = sortedItems.reduce((arr: PeriodSchedule[], i, index) => {
		let from;
		let maxFrom;

		const {
			id,
			tcTermsId,
			durationMinimumValue,
			durationMinimumUnit,
			durationMinimumVariance,
			minimumDurationExact,
			durationMaximumValue,
			durationMaximumUnit,
			durationMaximumVariance,
			maximumDurationExact,
			order,
			hireRate,
			periodType,
			hireType,
			description,
			exercised,
			useExactDate,
		} = i;

		if (order === 1) {
			from = deliveryDate || laycanTo;
			maxFrom = deliveryDate || laycanFrom;
		} else {
			const prevEntry = arr[index - 1];

			if (prevEntry == null) {
				return arr;
			}

			from = prevEntry.to;
			maxFrom = prevEntry.to;
		}

		if (from == null) {
			return arr;
		}

		const minDuration = durationMinimumValue;
		const minUnit = durationMinimumUnit;

		const maxDuration = durationMaximumValue;
		const maxUnit = durationMaximumUnit;

		if (minimumDurationExact == null && (minDuration == null || minUnit == null)) {
			return arr;
		}

		let to;

		if (minimumDurationExact != null) {
			to = minimumDurationExact;
		} else if (minDuration != null && minUnit != null) {
			to = addDuration(from, minDuration, minUnit);
		} else {
			return arr;
		}

		if (to == null) {
			return arr;
		}

		if (useVariance) {
			to = toMoment(to).subtract(durationMinimumVariance ?? 0, 'days');
		}

		let maxTo = to;

		if (maximumDurationExact != null) {
			maxTo = maximumDurationExact;
		} else if (maxDuration != null && maxUnit != null) {
			maxTo = addDuration(maxFrom ?? from, maxDuration, maxUnit);
			if (useVariance) {
				maxTo = maxTo.add(durationMaximumVariance ?? 0, 'days');
			}
		}

		return [...arr, {
			id,
			tcTermsId,
			durationMinimumValue,
			durationMinimumUnit,
			durationMinimumVariance,
			minimumDurationExact,
			durationMaximumValue,
			durationMaximumUnit,
			durationMaximumVariance,
			maximumDurationExact,
			order,
			hireRate,
			periodType,
			hireType,
			description,
			exercised,
			from: toMoment(from).utc(),
			to: toMoment(to).utc(),
			maxTo,
			useExactDate,
		}];
	}, []);

	return schedule;
};

export default calculateSchedule;
